import {
  Arrow,
  Content,
  Portal,
  Provider,
  Root as TooltipRoot,
  Trigger,
} from "@radix-ui/react-tooltip";
import { Children, type ComponentPropsWithoutRef } from "react";
import { cn } from "../lib/utils";

const TooltipProvider = Provider;
const Tooltip = TooltipRoot;
const TooltipTrigger = Trigger;

type TooltipContentProps = ComponentPropsWithoutRef<typeof Content> & {
  ref?: React.Ref<HTMLDivElement>;
};
const TooltipContent = ({
  children,
  className,
  sideOffset = 4,
  side = "top",
  ref,
  ...props
}: TooltipContentProps) => {
  const childArray = Children.toArray(children);
  const isSingleChild = childArray.length === 1;
  return (
    <Portal>
      <Content
        ref={ref}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          "z-[99999] overflow-hidden rounded bg-gray-900 text-caption px-2 py-1 text-xs font-poppins animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "min-w-10",
          "max-w-[500px]",
          "text-white",
          isSingleChild ? "text-center" : "text-left",
          className,
        )}
        {...props}
      >
        {!isSingleChild
          ? childArray.map((child, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: <This is an array of children, not an array of React elements>
              <div key={index}>
                {child}
                {index !== childArray.length - 1 && (
                  <div className="my-1 border-t border-t-gray-200" />
                )}
              </div>
            ))
          : children}
        <Arrow className="mb-1 fill-gray-900" width={8} height={4} />
      </Content>
    </Portal>
  );
};
TooltipContent.displayName = Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
